// @ts-strict-ignore
import * as React from 'react';
import {
    AppPlatform,
    AutomationStatus,
    AutomationType,
    CompletedReleaseSummary,
    LifecycleStep,
    NextReleaseSummary,
    Release,
    AutomationRun,
    UpcomingReleaseSummary,
    AutoSelectLatestBuildStrategy,
    AppStoreType,
} from '../../types';
import {
    CloseCircleFilled,
    ExclamationCircleFilled,
    ThunderboltFilled,
    ThunderboltOutlined,
    PauseOutlined,
    WarningFilled,
    RedoOutlined,
} from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectIsReleaseKickedOff, selectIsReleaseReleased, selectIsReleaseSubmitted } from '../../selectors/selectors';
import { displayTextForAutomationStatus } from '../../helpers/displayHelpers';
import { dayjs } from '@/extendedDayjs';
import Tooltip from 'antd/lib/tooltip';
import { capitalize } from 'lodash';

export const smallAutomationIconForStatus: (
    status: AutomationStatus,
    color: string,
    size?: number,
) => React.ReactNode = (status, color, size) => {
    let icon: React.ReactNode;

    switch (status) {
        case AutomationStatus.Success:
            icon = <ThunderboltFilled style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Failed:
            icon = <CloseCircleFilled style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Pending:
            icon = <ThunderboltFilled style={{ fontSize: 14, color }} />;
            break;
        case AutomationStatus.Inactive:
        case AutomationStatus.Skipped:
            icon = <ThunderboltOutlined style={{ fontSize: 14, color }} />;
            break;
        case AutomationStatus.Incomplete:
            icon = <WarningFilled style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Paused:
            icon = <PauseOutlined style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Blocked:
            icon = <ExclamationCircleFilled style={{ fontSize: size, color }} />;
            break;
        case AutomationStatus.Retrying:
            icon = <RedoOutlined style={{ fontSize: size, color }} />;
            break;
        default:
            return null;
    }

    return icon;
};

export const smallIconColorForStatus: (status: AutomationStatus) => string = (status) => {
    switch (status) {
        case AutomationStatus.Blocked:
        case AutomationStatus.Failed:
            return runwayColors.statusRed;
        case AutomationStatus.Incomplete:
            return runwayColors.statusYellow;
        case AutomationStatus.Success:
            return antGrey.grey6;
        case AutomationStatus.Retrying:
        case AutomationStatus.Paused:
            return runwayColors.statusYellow;
        case AutomationStatus.Pending:
            return runwayColors.info;
        case AutomationStatus.Skipped:
        case AutomationStatus.Inactive:
            return antGrey.grey6;
        default:
            return runwayColors.info;
    }
};

export const findLifecycleAutomationRun: (
    step: LifecycleStep,
    lifecycleAutomations: AutomationRun[],
) => AutomationRun = (step, lifecycleAutomations) => {
    if (!lifecycleAutomations) {
        return null;
    }

    let automationType: AutomationType;
    switch (step) {
        case LifecycleStep.Kickoff:
            automationType = AutomationType.AutoKickoffRelease;
            break;
        case LifecycleStep.Submission:
            automationType = AutomationType.AutoSubmitRelease;
            break;
        case LifecycleStep.Release:
            automationType = AutomationType.AutoRelease;
            break;
        default:
            return null;
    }

    const automation = lifecycleAutomations.find((automation) => automation.type === automationType);

    return automation;
};

export const automationTypeMatchesLifecycleStep: (step: LifecycleStep, automationType: AutomationType) => boolean = (
    step,
    automationType,
) => {
    switch (step) {
        case LifecycleStep.Kickoff:
            return automationType === AutomationType.AutoKickoffRelease;
        case LifecycleStep.Submission:
            return automationType === AutomationType.AutoSubmitRelease;
        case LifecycleStep.Release:
            return automationType === AutomationType.AutoRelease;
        default:
            return false;
    }
};

export const useLifecycleStepCompleted: (step: LifecycleStep, releaseId: string) => boolean = (step, releaseId) => {
    switch (step) {
        case LifecycleStep.Kickoff:
            return useSelector((state: RootState) => selectIsReleaseKickedOff(state, releaseId));
        case LifecycleStep.Submission:
            return useSelector((state: RootState) => selectIsReleaseSubmitted(state, releaseId));
        case LifecycleStep.Release:
            return useSelector((state: RootState) => selectIsReleaseReleased(state, releaseId));
        default:
            return null;
    }
};

export const getLifecycleStepCompleted: (
    step: LifecycleStep,
    release: Release | NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary,
) => boolean = (step, release) => {
    if (!release) {
        return null;
    }
    switch (step) {
        case LifecycleStep.Kickoff:
            if ('isKickedOff' in release) {
                return release.isKickedOff;
            }
            return null;
        case LifecycleStep.Submission:
            if ('isSubmitted' in release) {
                return release.isSubmitted;
            }
            return null;
        case LifecycleStep.Release:
            if ('isReleased' in release) {
                return release.isReleased;
            }
            return null;
        default:
            return null;
    }
};

export const getNextRelevantLifecycleStep: (
    release: Release | NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary,
) => LifecycleStep = (release) => {
    if (!release) {
        return null;
    }

    // the order is of relevance is kickoff, submission then release unless the step is already completed
    if (!release.isKickedOff || dayjs().isBefore(release.targetKickoffDate)) {
        return LifecycleStep.Kickoff;
    }

    if (('isSubmitted' in release && !release.isSubmitted) || dayjs().isBefore(release.targetSubmitDate)) {
        return LifecycleStep.Submission;
    }

    if (('isReleased' in release && !release.releasedAt) || dayjs().isBefore(release.targetReleaseDate)) {
        return LifecycleStep.Release;
    }

    return LifecycleStep.Release;
};

export const isLifecycleStepCompleted: (step: LifecycleStep, releaseSummary: NextReleaseSummary) => boolean = (
    step,
    releaseSummary,
) => {
    switch (step) {
        case LifecycleStep.Kickoff:
            return releaseSummary.isKickedOff;
        case LifecycleStep.Submission:
            return releaseSummary.isSubmitted;
        default:
            return null;
    }
};

export const isLifecycleStepCompletedDate: (step: LifecycleStep, releaseSummary: NextReleaseSummary) => string = (
    step,
    releaseSummary,
) => {
    switch (step) {
        case LifecycleStep.Kickoff:
            return releaseSummary.kickedOffAt;
        case LifecycleStep.Submission:
            return releaseSummary.submittedAt;
        default:
            return null;
    }
};

export const getLifecycleStepCompletedDate: (
    step: LifecycleStep,
    release: Release | NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary,
) => string = (step, release) => {
    if (!release) {
        return null;
    }
    switch (step) {
        case LifecycleStep.Kickoff:
            if ('kickedOffAt' in release) {
                return release.kickedOffAt;
            }
            return null;
        case LifecycleStep.Submission:
            if ('submittedAt' in release) {
                return release.submittedAt;
            }
            return null;
        case LifecycleStep.Release:
            if ('releasedAt' in release) {
                return release.releasedAt;
            }
            return null;
        default:
            return null;
    }
};

export const getLifecycleStepTargetDate: (
    step: LifecycleStep,
    release: Release | NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary,
) => dayjs.Dayjs = (step, release) => {
    if (!release) {
        return null;
    }
    switch (step) {
        case LifecycleStep.Kickoff:
            return dayjs(release.targetKickoffDate);
        case LifecycleStep.Submission:
            return dayjs(release.targetSubmitDate);
        case LifecycleStep.Release:
            return dayjs(release.targetReleaseDate);
        default:
            return null;
    }
};

export const getLifecycleStepDisplayText: (
    step: LifecycleStep,
    isComplete: boolean,
    automationSuccessful: boolean,
) => string = (step, isComplete, automationSuccessful) => {
    const autoText = automationSuccessful ? 'Auto ' : '';
    let displayText = null;
    switch (step) {
        case LifecycleStep.Kickoff:
            displayText = isComplete ? `${autoText}Kicked Off` : `Kickoff`;
            break;
        case LifecycleStep.Submission:
            displayText = isComplete ? `${autoText}Submitted` : `Submission`;
            break;
        case LifecycleStep.Release:
            displayText = isComplete ? `${autoText}Released` : `Release`;
            break;
    }
    return capitalize(displayText);
};

export const allLifecycleEventsForPlatform: (platform: AppPlatform) => LifecycleStep[] = (platform: AppPlatform) => {
    switch (platform) {
        case AppPlatform.TvOS:
        case AppPlatform.Ios:
        case AppPlatform.VisionOS:
            return [LifecycleStep.Kickoff, LifecycleStep.Submission, LifecycleStep.Release];
        case AppPlatform.WearOS:
        case AppPlatform.Android:
            return [LifecycleStep.Kickoff, LifecycleStep.Submission];
        case AppPlatform.ReactNativeOTA:
        case AppPlatform.AndroidSDK:
        case AppPlatform.IosSDK:
            return [LifecycleStep.Kickoff, LifecycleStep.Release];
        default:
            return [];
    }
};

interface LabelIconDateString {
    icon?: React.ReactNode;
    dateString: string;
    overrideColor?: string;
}

export const targetDateLabelIconDateString: (
    automation: AutomationRun,
    targetDate: dayjs.Dayjs,
) => LabelIconDateString = (automation, targetDate) => {
    const color = smallIconColorForStatus(automation.status);

    let iconNode: React.ReactNode = null;
    let dateString: string = displayTextForAutomationStatus(automation.status);
    let overrideColor = color;

    const iconSize = 14;

    switch (automation.status) {
        case AutomationStatus.Failed: {
            const icon = smallAutomationIconForStatus(automation.status, color, 14);

            iconNode = (
                <Tooltip title={automation.statusMessage ?? 'Something went wrong while executing this automation.'}>
                    {icon}
                </Tooltip>
            );

            break;
        }
        case AutomationStatus.Blocked: {
            const icon = smallAutomationIconForStatus(automation.status, color, iconSize);
            iconNode = (
                <Tooltip title={automation.statusMessage ?? 'This automation is blocked and cannot proceed.'}>
                    {icon}
                </Tooltip>
            );

            break;
        }
        case AutomationStatus.Incomplete: {
            const icon = smallAutomationIconForStatus(automation.status, color, iconSize);

            iconNode = (
                <Tooltip title={automation.statusMessage ?? 'Action required to complete automation.'}>{icon}</Tooltip>
            );

            break;
        }
        case AutomationStatus.Paused: {
            const icon = smallAutomationIconForStatus(automation.status, color, iconSize);

            iconNode = <Tooltip title={automation.statusMessage ?? 'This automation has been paused.'}>{icon}</Tooltip>;

            break;
        }
        case AutomationStatus.Success:
        case AutomationStatus.Pending: {
            iconNode = smallAutomationIconForStatus(automation.status, color, iconSize);
            overrideColor = null; // no color override for these states
            dateString = targetDate.format('h:mm a'); // regular date display for these states

            break;
        }
        case AutomationStatus.Skipped:
        case AutomationStatus.Inactive: {
            //don't show icon
            break;
        }
        case AutomationStatus.Retrying: {
            const icon = smallAutomationIconForStatus(automation.status, color, iconSize);
            iconNode = (
                <Tooltip
                    title={
                        automation.statusMessage ??
                        'Something went wrong while executing this automation. Runway will retry up to 5 times.'
                    }
                >
                    {icon}
                </Tooltip>
            );
            break;
        }
    }
    return {
        icon: iconNode,
        dateString,
        overrideColor,
    };
};

export const getAutoSelectLatestBuildStrategyFieldData = (ast: AppStoreType) => {
    switch (ast) {
        case AppStoreType.Apple:
            return {
                [AutoSelectLatestBuildStrategy.LatestAvailableBuild]: {
                    value: AutoSelectLatestBuildStrategy.LatestAvailableBuild,
                    displayValue: 'Select the latest build available on TestFlight for the release version',
                },
                [AutoSelectLatestBuildStrategy.LatestPassedRegression]: {
                    value: AutoSelectLatestBuildStrategy.LatestPassedRegression,
                    displayValue: 'Select the latest build that passed regression testing for the release version',
                },
            };
        case AppStoreType.Google:
            return {
                [AutoSelectLatestBuildStrategy.LatestAvailableBuild]: {
                    value: AutoSelectLatestBuildStrategy.LatestAvailableBuild,
                    displayValue: 'Select the latest APK/AAB in the Play Console',
                },
                [AutoSelectLatestBuildStrategy.LatestPassedRegression]: {
                    value: AutoSelectLatestBuildStrategy.LatestPassedRegression,
                    displayValue: 'Select the latest APK/AAB that passed regression testing',
                },
            };
        case AppStoreType.Amazon:
            return {
                [AutoSelectLatestBuildStrategy.LatestAvailableBuild]: {
                    value: AutoSelectLatestBuildStrategy.LatestAvailableBuild,
                    displayValue: 'Select the latest APK in the Amazon Developer Console',
                },
                [AutoSelectLatestBuildStrategy.LatestPassedRegression]: {
                    value: AutoSelectLatestBuildStrategy.LatestPassedRegression,
                    displayValue: 'Select the latest APK that passed regression testing',
                },
            };
        case AppStoreType.Samsung:
            // not supported for Samsung
            return {};
        case AppStoreType.Huawei:
            // not supported for Huawei
            return {};
        default:
            return {};
    }
};
